import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Snowflake,
  Camera,
  CheckCircle2,
  XCircle,
  AlertCircle,
  User,
  Ticket,
  Users,
  Calendar,
  Clock,
  CreditCard,
  ArrowLeft,
  RefreshCw,
  QrCode,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckInPage = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const html5QrCodeRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      setScanResult(null);
      setScanning(true);

      // Wait for DOM element to be ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        onScanSuccess,
        onScanFailure
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      toast.error("Kamera konnte nicht gestartet werden");
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    // Stop scanning after successful read
    await stopScanning();
    
    // Process the scanned code
    await validateTicket(decodedText);
  };

  const onScanFailure = (error) => {
    // Silently ignore scan failures (no QR code in frame)
  };

  const validateTicket = async (qrData) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/checkin/validate/${encodeURIComponent(qrData)}`);
      setScanResult(response.data);
    } catch (error) {
      setScanResult({
        valid: false,
        message: "Fehler bei der Verbindung zum Server"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualValidate = async () => {
    if (!manualCode.trim()) {
      toast.error("Bitte Code eingeben");
      return;
    }
    await validateTicket(manualCode.trim());
  };

  const handleCheckIn = async () => {
    if (!scanResult?.valid || !scanResult?.booking_id) {
      toast.error("Keine gültige Buchung zum Einchecken");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkin/confirm`, {
        booking_id: scanResult.booking_id,
        booking_type: scanResult.booking_type
      });

      if (response.data.success) {
        toast.success("Check-in erfolgreich!");
        setScanResult({
          ...scanResult,
          checkedIn: true,
          checkInMessage: response.data.message
        });
      } else {
        toast.error(response.data.message);
        setScanResult({
          ...scanResult,
          checkInError: response.data.message
        });
      }
    } catch (error) {
      toast.error("Fehler beim Check-in");
    } finally {
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setScanResult(null);
    setManualCode("");
  };

  const getPaymentBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800">Bezahlt</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800">Ausstehend</Badge>;
      case "cancelled":
        return <Badge className="bg-red-100 text-red-800">Storniert</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-800">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Snowflake className="w-8 h-8 text-sky-400" />
            <div>
              <h1 className="text-xl font-semibold text-white" style={{ fontFamily: 'Playfair Display' }}>
                Check-in
              </h1>
              <p className="text-sm text-slate-400">Grömitzer Eiszeit</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Scanner Section */}
        {!scanResult && (
          <Card className="border-0 shadow-2xl bg-slate-800/50 backdrop-blur" data-testid="scanner-card">
            <CardHeader className="text-center">
              <CardTitle className="text-white flex items-center justify-center gap-2">
                <QrCode className="w-6 h-6 text-sky-400" />
                QR-Code Scannen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Camera View */}
              <div className="relative">
                <div
                  id="qr-reader"
                  className={`w-full aspect-square rounded-xl overflow-hidden bg-slate-900 ${
                    scanning ? "" : "hidden"
                  }`}
                />
                
                {!scanning && (
                  <div className="w-full aspect-square rounded-xl bg-slate-900 flex flex-col items-center justify-center border-2 border-dashed border-slate-600">
                    <Camera className="w-16 h-16 text-slate-500 mb-4" />
                    <p className="text-slate-400 text-center px-4">
                      Klicken Sie auf "Kamera starten" um einen QR-Code zu scannen
                    </p>
                  </div>
                )}
              </div>

              {/* Scanner Controls */}
              <div className="flex gap-3">
                {!scanning ? (
                  <Button
                    onClick={startScanning}
                    className="flex-1 h-12 bg-sky-500 hover:bg-sky-600"
                    data-testid="start-scan-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Kamera starten
                  </Button>
                ) : (
                  <Button
                    onClick={stopScanning}
                    variant="outline"
                    className="flex-1 h-12 border-slate-600 text-slate-300"
                    data-testid="stop-scan-btn"
                  >
                    Kamera stoppen
                  </Button>
                )}
              </div>

              {/* Manual Entry */}
              <div className="border-t border-slate-700 pt-6">
                <p className="text-sm text-slate-400 mb-3 text-center">
                  Oder Code manuell eingeben:
                </p>
                <div className="flex gap-2">
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="z.B. SILVESTER:abc123..."
                    className="flex-1 bg-slate-900 border-slate-700 text-white"
                    data-testid="manual-code-input"
                  />
                  <Button
                    onClick={handleManualValidate}
                    disabled={loading}
                    className="bg-sky-500 hover:bg-sky-600"
                    data-testid="validate-btn"
                  >
                    Prüfen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result Section */}
        {scanResult && (
          <Card 
            className={`border-0 shadow-2xl ${
              scanResult.valid 
                ? scanResult.checkedIn 
                  ? "bg-green-900/50" 
                  : "bg-slate-800/50"
                : "bg-red-900/50"
            } backdrop-blur`}
            data-testid="result-card"
          >
            <CardContent className="pt-6">
              {/* Status Icon */}
              <div className="text-center mb-6">
                {scanResult.valid ? (
                  scanResult.checkedIn ? (
                    <>
                      <CheckCircle2 className="w-20 h-20 text-green-400 mx-auto mb-3" />
                      <h2 className="text-2xl font-bold text-green-400">Eingecheckt!</h2>
                    </>
                  ) : scanResult.booking_info?.checked_in ? (
                    <>
                      <AlertCircle className="w-20 h-20 text-amber-400 mx-auto mb-3" />
                      <h2 className="text-2xl font-bold text-amber-400">Bereits eingecheckt</h2>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-20 h-20 text-sky-400 mx-auto mb-3" />
                      <h2 className="text-2xl font-bold text-sky-400">Gültiges Ticket</h2>
                    </>
                  )
                ) : (
                  <>
                    <XCircle className="w-20 h-20 text-red-400 mx-auto mb-3" />
                    <h2 className="text-2xl font-bold text-red-400">Ungültig</h2>
                    <p className="text-red-300 mt-2">{scanResult.message}</p>
                  </>
                )}
              </div>

              {/* Booking Details */}
              {scanResult.valid && scanResult.booking_info && (
                <div className="bg-slate-900/50 rounded-xl p-4 space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-white">
                    <Ticket className="w-5 h-5 text-sky-400" />
                    <span className="font-semibold">{scanResult.booking_info.type}</span>
                    <span className="text-slate-400">-</span>
                    <span>{scanResult.booking_info.type_detail}</span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-white">
                    <User className="w-5 h-5 text-sky-400" />
                    <span>{scanResult.booking_info.name}</span>
                  </div>
                  
                  {scanResult.booking_info.ticket_count && (
                    <div className="flex items-center gap-3 text-white">
                      <Users className="w-5 h-5 text-sky-400" />
                      <span>{scanResult.booking_info.ticket_count} Tickets</span>
                    </div>
                  )}
                  
                  {scanResult.booking_info.group_size && (
                    <div className="flex items-center gap-3 text-white">
                      <Users className="w-5 h-5 text-sky-400" />
                      <span>{scanResult.booking_info.group_size} Personen</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 text-white">
                    <CreditCard className="w-5 h-5 text-sky-400" />
                    <span>{scanResult.booking_info.total_price?.toFixed(2)} €</span>
                    <span className="ml-auto">
                      {getPaymentBadge(scanResult.booking_info.payment_status)}
                    </span>
                  </div>
                  
                  {scanResult.booking_info.checked_in && scanResult.booking_info.checked_in_at && (
                    <div className="flex items-center gap-3 text-amber-400 border-t border-slate-700 pt-3 mt-3">
                      <Clock className="w-5 h-5" />
                      <span>Eingecheckt: {new Date(scanResult.booking_info.checked_in_at).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={resetScanner}
                  variant="outline"
                  className="flex-1 h-12 border-slate-600 text-slate-300"
                  data-testid="reset-btn"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Neuer Scan
                </Button>
                
                {scanResult.valid && !scanResult.checkedIn && !scanResult.booking_info?.checked_in && (
                  <Button
                    onClick={handleCheckIn}
                    disabled={loading}
                    className="flex-1 h-12 bg-green-500 hover:bg-green-600"
                    data-testid="checkin-btn"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    {loading ? "Wird eingecheckt..." : "Einchecken"}
                  </Button>
                )}
              </div>

              {/* Error Message */}
              {scanResult.checkInError && (
                <div className="mt-4 p-3 bg-red-900/50 rounded-lg text-red-300 text-center">
                  {scanResult.checkInError}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <div className="mt-8 text-center text-slate-400 text-sm">
          <p>Scannen Sie den QR-Code auf dem Ticket des Gastes</p>
          <p className="mt-1">oder geben Sie den Code manuell ein.</p>
        </div>
      </main>
    </div>
  );
};

export default CheckInPage;
